/**
 * File: useProjectsQueries.ts
 * Path: apps/admin/src/shared/hooks/useProjectsQueries.ts
 * Description: Read-only data fetching for Authoritative Portfolio projects using React Query.
 * Extended to include the users collection fetch for author assignment.
 */

import { useQuery } from "@tanstack/react-query";
import {
  ADMIN_PAGE_SIZE,
  FirestoreService,
  FIRESTORE_COLLECTIONS,
  limit,
  orderBy,
  where,
} from "@idk/config";
import { AuthoritativePortfolioProject } from "@idk/types";
import { useAuthorDirectory } from "./useAuthorDirectoryQueries";

export const useProjectsQueries = () => {
  return {
    useList: () =>
      useQuery({
        queryKey: [FIRESTORE_COLLECTIONS.portfolio, "list"],
        queryFn: () =>
          FirestoreService.getAll<AuthoritativePortfolioProject>(
            FIRESTORE_COLLECTIONS.portfolio,
            ADMIN_PAGE_SIZE,
          ),
      }),

    useById: (id?: string) =>
      useQuery({
        queryKey: [FIRESTORE_COLLECTIONS.portfolio, "detail", id],
        queryFn: () =>
          FirestoreService.getById<AuthoritativePortfolioProject>(
            FIRESTORE_COLLECTIONS.portfolio,
            id!,
          ),
        enabled: !!id,
      }),

    useBySlug: (slug?: string) =>
      useQuery({
        queryKey: [FIRESTORE_COLLECTIONS.portfolio, "slug", slug],
        queryFn: () =>
          FirestoreService.findOne<AuthoritativePortfolioProject>(
            FIRESTORE_COLLECTIONS.portfolio,
            "slug",
            slug,
          ),
        enabled: !!slug,
      }),

    useFeaturedOnly: () =>
      useQuery({
        queryKey: [FIRESTORE_COLLECTIONS.portfolio, "featured"],
        queryFn: () =>
          FirestoreService.query<AuthoritativePortfolioProject>(
            FIRESTORE_COLLECTIONS.portfolio,
            where("featured", "==", true),
            orderBy("createdAt", "desc"),
            limit(ADMIN_PAGE_SIZE),
          ),
      }),

    // Author selection dropdown; the same shared directory the academy editor
    // uses, so the P2.6 fallback exists in one place.
    useUsers: useAuthorDirectory,
  };
};
